import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getDrawdownLoanByDrawdownId,
  getPayout,
  getRetailOdDrawdownLoanRepaymentSplitBySubLoanId,
  getSubLoanLedgerInfoBySubLoanId,
  getSubLoansTransformedRepaymentScheduleBySubLoanId,
  getUnpaidRepaymentCommitmentAmountByEntityId
} from "../../../actions/loan.action";
import { showNotifier } from "../../../actions/ui.action";
import Tabs, { TabItem } from "../../../components/tabs";
import {
  DrawdownLoan,
  Payout,
  RepaymentCommitment,
  RetailOdDrawdownLoanRepaymentSplit,
  SubLoanLedgerInfo,
  SubLoanRepaymentSchedule
} from "../../../dto";
import useQueryParams from "../../../hooks/useQueryParams";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import dispatch from "../../../middleware";
import { NotifierBgColor } from "../../../models";
import { ReduxState } from "../../../reducers";
import CreditAccountSummary from "../creditAccountSummary";
import { useRepaymentCommitments } from "../hooks/useRepaymentCommitments";
import CurrentLoanAccountStatus from "../loan-details/currentLoanStatus";
import DisbursalDetails from "../loan-details/disbursalDetails";
import LoanDetailsHeader from "../loan-details/loanDetailsHeader";
import LoanSummary from "../loan-details/loanSummary";
import LoanDocuments from "../loan-documents";
import LoanRemarks from "../loan-remarks";
import LoanRepaymentCommitment from "../loanRepaymentCommitment";
import { Tag } from "../loanTags";
import RetailOdDrawdownLoanRepaymentSchedule from "../retailOdDrawdownLoanRepaymentSchedule";
import RetailOdDrawdownRepaymentSplit from "../retailOdDrawdownLoanRepaymentSplit";

type TabIndex = "DRAWDOWN_DETAILS" | "CREDIT_ASSESSMENT" | "DOCUMENTS";

type QueryState = {
  tab: TabIndex;
};

const DEFAULT_QUERY_STATE_STATE: QueryState = {
  tab: "DRAWDOWN_DETAILS",
};

const tabItems: TabItem<TabIndex>[] = [
  {
    label: "Drawdown Details",
    value: "DRAWDOWN_DETAILS",
  },
  { label: "Documents", value: "DOCUMENTS" },
];

export default function DrawdownDetails() {
  const { drawdownId }: { drawdownId: string } = useParams();
  const storeDispatch = useDispatch();
  const singleDrawdownLoan: DrawdownLoan | null = useSelector(
    (state: ReduxState) => state.loan.singleDrawdownLoan,
  );
  const payout: Payout | null = useSelector(
    (state: ReduxState) => state.loan.payout,
  );
  const subLoanLedgerInfo: SubLoanLedgerInfo | null = useSelector(
    (state: ReduxState) => state.loan.subLoanLedgerInfo,
  );
  const retailOdDrawdownLoanRepaymentSplit:
    RetailOdDrawdownLoanRepaymentSplit[] | null = useSelector(
    (state: ReduxState) => state.loan.retailOdDrawdownLoanRepaymentSplit,
  );
  const subLoanRepaymentSchedule: SubLoanRepaymentSchedule[] | null =
    useSelector((state: ReduxState) => state.loan.subLoanRepaymentSchedule);
  const unpaidRepaymentCommitmentAmount: number | null = useSelector(
    (state: ReduxState) => state.loan.unpaidRepaymentCommitmentAmount,
  );
  const repaymentCommitments: RepaymentCommitment[] | null = useSelector(
    (state: ReduxState) => state.loan.repaymentCommitments,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [{ tab }, setSearchQuery] = useQueryParams<QueryState>(
    DEFAULT_QUERY_STATE_STATE,
  );
  const { fetchLoanRepaymentCommitments } = useRepaymentCommitments();

  const fetchPayoutInfo = useCallback(
    async (referenceId: string) => {
      setIsLoading(true);
      try {
        if (!referenceId) return;

        await dispatch(storeDispatch, getPayout(referenceId));
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error.message || "Error fetching drawdown details",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [storeDispatch],
  );

  const fetchSubLoanLedgerInfo = useCallback(
    async (subloanId: string) => {
      setIsLoading(true);
      try {
        if (!subloanId) return;
        await dispatch(
          storeDispatch,
          getSubLoanLedgerInfoBySubLoanId(subloanId),
        );
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error.message || "Error fetching drawdown details",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [storeDispatch],
  );

  const fetchRetailOdDrawdownLoanRepaymentSplit = useCallback(
    async (subloanId: string) => {
      setIsLoading(true);

      try {
        if (!subloanId) return;
        await dispatch(
          storeDispatch,
          getRetailOdDrawdownLoanRepaymentSplitBySubLoanId(subloanId),
        );
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error.message || "Error fetching drawdown details",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [storeDispatch],
  );

  const fetchSubLoanRepaymentSchedule = useCallback(
    async (subloanId: string) => {
      setIsLoading(true);

      try {
        if (!subloanId) return;
        await dispatch(
          storeDispatch,
          getSubLoansTransformedRepaymentScheduleBySubLoanId(subloanId),
        );
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error.message || "Error fetching drawdown details",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [storeDispatch],
  );

  const fetchUnpaidRepaymentCommitmentAmount = useCallback(
    async (entityId: string) => {
      try {
        await dispatch(
          storeDispatch,
          getUnpaidRepaymentCommitmentAmountByEntityId(entityId),
        );
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error?.message || "An unknown error occurred",
        );
      }
    },
    [storeDispatch],
  );

  useEffect(() => {
    if (!drawdownId) return;
    dispatch(storeDispatch, getDrawdownLoanByDrawdownId(drawdownId));
  }, [drawdownId, storeDispatch]);

  useEffect(() => {
    if (!singleDrawdownLoan?.referenceId) return;
    fetchPayoutInfo(singleDrawdownLoan.referenceId);
  }, [fetchPayoutInfo, singleDrawdownLoan?.referenceId]);

  useEffect(() => {
    if (!singleDrawdownLoan?._id) return;
    fetchSubLoanLedgerInfo(singleDrawdownLoan._id);
  }, [fetchSubLoanLedgerInfo, singleDrawdownLoan?._id]);

  useEffect(() => {
    if (!singleDrawdownLoan?._id) return;
    fetchRetailOdDrawdownLoanRepaymentSplit(singleDrawdownLoan._id);
  }, [fetchRetailOdDrawdownLoanRepaymentSplit, singleDrawdownLoan?._id]);

  useEffect(() => {
    if (!singleDrawdownLoan?._id) return;
    fetchSubLoanRepaymentSchedule(singleDrawdownLoan._id);
  }, [fetchSubLoanRepaymentSchedule, singleDrawdownLoan?._id]);

  useEffect(() => {
    if (!singleDrawdownLoan?._id) return;
    fetchUnpaidRepaymentCommitmentAmount(singleDrawdownLoan._id);
  }, [fetchUnpaidRepaymentCommitmentAmount, singleDrawdownLoan?._id]);

  useEffect(() => {
    if (!singleDrawdownLoan?._id) return;
    fetchLoanRepaymentCommitments(singleDrawdownLoan._id);
  }, [fetchLoanRepaymentCommitments, singleDrawdownLoan?._id]);

  if (isLoading)
    return (
      <div className="d-flex align-items-center justify-content-center">
        <Loader />
      </div>
    );

  if (!singleDrawdownLoan)
    return (
      <div className="d-flex align-items-center justify-content-center">
        <p className="text-muted">Not able to load drawdown</p>
      </div>
    );

  return (
    <>
      <div className="dashboard-container p-3">
        <Tabs
          items={tabItems}
          value={tab}
          onChange={(value: TabIndex) => {
            setSearchQuery({ ...DEFAULT_QUERY_STATE_STATE, tab: value });
          }}
        />
      </div>

      {singleDrawdownLoan.isForeclosed && (
        <div className="dashboard-container">
          <Tag label="Foreclosed" color="#808080" />
        </div>
      )}
      <div className="dashboard-container">
        <LoanDetailsHeader
          drawdownLoanDetails={singleDrawdownLoan}
          isDrawdownLoan={true}
        />
      </div>
      {tab === "DRAWDOWN_DETAILS" && (
        <>
          {singleDrawdownLoan && (
            <div className="dashboard-container">
              <LoanSummary
                drawdownLoanDetails={singleDrawdownLoan}
                isDrawdownLoan={true}
              />
            </div>
          )}

          {payout && (
            <div className="dashboard-container">
              <DisbursalDetails payoutDetails={payout} isDrawdownLoan={true} />
            </div>
          )}

          {subLoanLedgerInfo && (
            <div className="dashboard-container">
              <CurrentLoanAccountStatus
                subLoanLedgerInfo={subLoanLedgerInfo}
                isDrawdownLoan={true}
                unpaidRepaymentCommitmentAmount={
                  unpaidRepaymentCommitmentAmount
                }
              />
            </div>
          )}

          <div className="dashboard-container">
            <RetailOdDrawdownLoanRepaymentSchedule
              data={subLoanRepaymentSchedule}
              isLoading={isLoading}
            />
          </div>

          <div className="dashboard-container">
            <RetailOdDrawdownRepaymentSplit
              data={retailOdDrawdownLoanRepaymentSplit}
              isLoading={isLoading}
            />
          </div>

          <div className="dashboard-container">
            <LoanRepaymentCommitment
              repaymentCommitments={repaymentCommitments}
            />
          </div>
          <div className="dashboard-container">
            <LoanRemarks loanId={drawdownId} loanType={"RETAIL_OD_DRAWDOWN"} />
          </div>
        </>
      )}
      {tab === "CREDIT_ASSESSMENT" && (
        <CreditAccountSummary
          loanId={drawdownId}
          loanType={"RETAIL_OD_DRAWDOWN"}
        />
      )}
      {tab === "DOCUMENTS" && (
        <LoanDocuments loanType={"TRANSACTION"} loanId={drawdownId} />
      )}
    </>
  );
}
